/**
 * Pay Periods API service
 * Handles pay period management for timesheets
 */

import {
  AllUsersTimesheetRow,
  GeneratePayPeriodsRequest,
  MissingTimesheet,
  PayPeriod,
  PayPeriodSummary,
  PayPeriodTimesheetSummary,
  PayPeriodTypeEnum,
  TimeEntry,
  UserPayPeriod,
} from '@scrambl/shared'

import { apiClient, emptyResponseToNull } from './api-client'

class PayPeriodApiService {
  private static instance: PayPeriodApiService | null = null
  private readonly baseEndpoint = '/pay-periods'

  public static getInstance(): PayPeriodApiService {
    if (!PayPeriodApiService.instance) {
      PayPeriodApiService.instance = new PayPeriodApiService()
    }
    return PayPeriodApiService.instance
  }

  /**
   * Get all pay periods for an organization
   */
  async getPayPeriods(
    params: {
      status?: string
      startDate?: string
      endDate?: string
      page?: number
      limit?: number
    } = {},
  ): Promise<{ data: PayPeriod[]; total: number }> {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())

    const url = `${this.baseEndpoint}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient<{ data: PayPeriod[]; total: number }>(url)
  }

  /**
   * Get the current open pay period
   */
  async getCurrentPeriod(): Promise<PayPeriod> {
    return apiClient<PayPeriod>(`${this.baseEndpoint}/current`)
  }

  /**
   * Get a specific pay period by ID
   */
  async getPayPeriod(id: string): Promise<PayPeriod> {
    return apiClient<PayPeriod>(`${this.baseEndpoint}/${id}`)
  }

  /**
   * Create a new pay period
   */
  async createPayPeriod(data: {
    name: string
    periodType: PayPeriodTypeEnum
    startDate: string
    endDate: string
  }): Promise<PayPeriod> {
    return apiClient<PayPeriod>(this.baseEndpoint, {
      method: 'POST',
      body: data,
    })
  }

  /**
   * Sync missing user pay period links for all active members
   */
  async syncUserPayPeriods(): Promise<{ created: number; members: number }> {
    return apiClient<{ created: number; members: number }>(
      `${this.baseEndpoint}/sync`,
      { method: 'POST' },
    )
  }

  /**
   * Regenerate pay periods based on current org settings
   */
  async regeneratePayPeriods(): Promise<{
    deleted: number
    created: number
    entriesReassigned: number
  }> {
    return apiClient<{
      deleted: number
      created: number
      entriesReassigned: number
    }>(`${this.baseEndpoint}/regenerate`, { method: 'POST' })
  }

  /**
   * Generate pay periods for a date range
   */
  async generatePayPeriods(request: GeneratePayPeriodsRequest): Promise<{
    created: number
    skipped: number
    periods: PayPeriod[]
  }> {
    return apiClient<{
      created: number
      skipped: number
      periods: PayPeriod[]
    }>(`${this.baseEndpoint}/generate`, {
      method: 'POST',
      body: request,
    })
  }

  /**
   * Close a pay period
   */
  async closePeriod(id: string, autoSubmit?: boolean): Promise<PayPeriod> {
    return apiClient<PayPeriod>(`${this.baseEndpoint}/${id}/close`, {
      method: 'PUT',
      body: autoSubmit !== undefined ? { autoSubmit } : undefined,
    })
  }

  /**
   * Reopen a closed pay period
   */
  async reopenPeriod(id: string): Promise<PayPeriod> {
    return apiClient<PayPeriod>(`${this.baseEndpoint}/${id}/reopen`, {
      method: 'PUT',
    })
  }

  /**
   * Lock a pay period permanently
   */
  async lockPeriod(id: string): Promise<PayPeriod> {
    return apiClient<PayPeriod>(`${this.baseEndpoint}/${id}/lock`, {
      method: 'PUT',
    })
  }

  /**
   * Get summary for a pay period
   */
  async getPeriodSummary(id: string): Promise<PayPeriodSummary> {
    return apiClient<PayPeriodSummary>(`${this.baseEndpoint}/${id}/summary`)
  }

  /**
   * Get members with missing timesheets for a pay period
   */
  async getMissingTimesheets(id: string): Promise<MissingTimesheet[]> {
    return apiClient<MissingTimesheet[]>(`${this.baseEndpoint}/${id}/missing`)
  }

  /**
   * Get all users' current timesheets (admin view)
   */
  async getAllUsersCurrentTimesheets(): Promise<AllUsersTimesheetRow[]> {
    return apiClient<AllUsersTimesheetRow[]>(
      `${this.baseEndpoint}/current/all-users`,
    )
  }

  /**
   * Get pay period summaries for the timesheets view (lightweight)
   */
  async getTimesheetPeriodSummaries(
    params: {
      status?: string
      periodType?: string
      startDate?: string
      endDate?: string
    } = {},
  ): Promise<PayPeriodTimesheetSummary[]> {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.periodType) queryParams.append('periodType', params.periodType)
    if (params.startDate) queryParams.append('startDate', params.startDate)
    if (params.endDate) queryParams.append('endDate', params.endDate)
    const url = `${this.baseEndpoint}/timesheets/periods${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient<PayPeriodTimesheetSummary[]>(url)
  }

  /**
   * Get user rows for a specific pay period (on-demand)
   */
  async getTimesheetPeriodUsers(
    periodId: string,
    params: {
      search?: string
      status?: string
      memberIds?: string[]
    } = {},
  ): Promise<AllUsersTimesheetRow[]> {
    const queryParams = new URLSearchParams()
    if (params.search) queryParams.append('search', params.search)
    if (params.status) queryParams.append('status', params.status)
    if (params.memberIds && params.memberIds.length > 0)
      queryParams.append('memberIds', params.memberIds.join(','))
    const url = `${this.baseEndpoint}/timesheets/periods/${periodId}/users${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient<AllUsersTimesheetRow[]>(url)
  }

  /**
   * Get the current user's pay period (user-specific status)
   */
  async getCurrentUserPeriod(): Promise<UserPayPeriod | null> {
    return emptyResponseToNull(
      await apiClient<UserPayPeriod | null>(`${this.baseEndpoint}/current/user`),
    )
  }

  /**
   * Get all UserPayPeriod records for the current user (with auto-backfill)
   */
  async getMyUserPayPeriods(
    params: { status?: string; page?: number; limit?: number } = {},
  ): Promise<{ data: UserPayPeriod[]; total: number }> {
    const queryParams = new URLSearchParams()
    if (params.status) queryParams.append('status', params.status)
    if (params.page) queryParams.append('page', params.page.toString())
    if (params.limit) queryParams.append('limit', params.limit.toString())
    const url = `${this.baseEndpoint}/my-periods${queryParams.toString() ? `?${queryParams.toString()}` : ''}`
    return apiClient<{ data: UserPayPeriod[]; total: number }>(url)
  }

  /**
   * Get time entries for the current user within a specific pay period
   */
  async getMyPeriodEntries(payPeriodId: string): Promise<TimeEntry[]> {
    return apiClient<TimeEntry[]>(
      `${this.baseEndpoint}/${payPeriodId}/my-entries`,
    )
  }

  /**
   * Get time entries for a specific user within a specific effective pay period
   */
  async getUserPeriodEntries(
    payPeriodId: string,
    userId: string,
  ): Promise<TimeEntry[]> {
    return apiClient<TimeEntry[]>(
      `${this.baseEndpoint}/${payPeriodId}/users/${userId}/entries`,
    )
  }

  /**
   * Refresh stored rate snapshots for a user's entries in a pay period.
   */
  async refreshUserPeriodRates(
    payPeriodId: string,
    userId: string,
  ): Promise<{
    updated: number
    failed: { entryId: string; reason: string }[]
  }> {
    return apiClient<{
      updated: number
      failed: { entryId: string; reason: string }[]
    }>(`${this.baseEndpoint}/${payPeriodId}/users/${userId}/refresh-rates`, {
      method: 'PUT',
    })
  }

  /**
   * Get the current user's UPP status for a specific pay period
   */
  async getMyPeriodStatus(payPeriodId: string): Promise<UserPayPeriod> {
    return apiClient<UserPayPeriod>(
      `${this.baseEndpoint}/${payPeriodId}/my-status`,
    )
  }

  /**
   * Get all user pay periods for a given pay period (admin)
   */
  async getUserPayPeriods(
    payPeriodId: string,
  ): Promise<{ data: UserPayPeriod[]; total: number }> {
    return apiClient<{ data: UserPayPeriod[]; total: number }>(
      `${this.baseEndpoint}/${payPeriodId}/users`,
    )
  }

  /**
   * Get a specific user's pay period
   */
  async getUserPayPeriod(
    payPeriodId: string,
    userId: string,
  ): Promise<UserPayPeriod> {
    return apiClient<UserPayPeriod>(
      `${this.baseEndpoint}/${payPeriodId}/users/${userId}`,
    )
  }

  /**
   * Submit a user's pay period (admin action)
   */
  async submitUserPeriod(
    payPeriodId: string,
    userId: string,
  ): Promise<UserPayPeriod> {
    return apiClient<UserPayPeriod>(
      `${this.baseEndpoint}/${payPeriodId}/users/${userId}/submit`,
      {
        method: 'PUT',
      },
    )
  }

  /**
   * Approve a user's pay period (admin action)
   */
  async approveUserPeriod(
    payPeriodId: string,
    userId: string,
  ): Promise<UserPayPeriod> {
    return apiClient<UserPayPeriod>(
      `${this.baseEndpoint}/${payPeriodId}/users/${userId}/approve`,
      {
        method: 'PUT',
      },
    )
  }

  /**
   * Reopen a user's pay period (admin action)
   */
  async reopenUserPeriod(
    payPeriodId: string,
    userId: string,
  ): Promise<UserPayPeriod> {
    return apiClient<UserPayPeriod>(
      `${this.baseEndpoint}/${payPeriodId}/users/${userId}/reopen`,
      {
        method: 'PUT',
      },
    )
  }

  /**
   * Recall the current user's own submitted timesheet (self-service)
   */
  async recallMySubmission(payPeriodId: string): Promise<UserPayPeriod> {
    return apiClient<UserPayPeriod>(
      `${this.baseEndpoint}/${payPeriodId}/my-recall`,
      {
        method: 'PUT',
      },
    )
  }
}

// Export singleton instance
export const payPeriodApi = PayPeriodApiService.getInstance()

// Export class for typing
export type { PayPeriodApiService }
